import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { translations } from "@/i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const ACCENTS = ["teal", "blue", "purple", "orange", "green"];
const ALL_THEMES = ACCENTS;
const sanitizeAccent = (c) => (ACCENTS.includes(c) ? c : "teal");

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("sb_lang") || "de");
  const [mode, setModeState] = useState("minimal");
  const [theme, setThemeState] = useState(sanitizeAccent(localStorage.getItem("sb_theme")));
  const THEMES = ACCENTS;
  const saveTheme = (m, c) => { api.put("/settings/theme", { mode: m, color: c }).catch(() => {}); };
  const setMode = () => {};
  const setTheme = (c) => { const nc = sanitizeAccent(c); setThemeState(nc); saveTheme("minimal", nc); };
  const [darkMode, setDarkModeState] = useState(localStorage.getItem("sb_dark") === "1");
  const setDarkMode = (v) => { localStorage.setItem("sb_dark", v ? "1" : "0"); setDarkModeState(!!v); };
  const [shopEnabled, setShopEnabled] = useState(false);
  const [groupsEnabled, setGroupsEnabled] = useState(false);
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [content, setContent] = useState(null);
  const [simpleMode, setSimpleModeState] = useState(localStorage.getItem("sb_simple") === "1");
  const setSimpleMode = (v) => { localStorage.setItem("sb_simple", v ? "1" : "0"); setSimpleModeState(!!v); };
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("sb_cart") || "[]"); } catch { return []; } });

  const t = useCallback((key) => {
    const ov = content?.[lang]?.i18n;
    if (ov && ov[key]) return ov[key];
    return translations[lang][key] || key;
  }, [lang, content]);

  const loadContent = useCallback(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadContent(); }, [loadContent]);

  useEffect(() => {
    ALL_THEMES.forEach((th) => document.body.classList.remove(`accent-${th}`));
    document.body.classList.remove("mode-classic", "mode-modern", "mode-minimal");
    const acc = sanitizeAccent(theme);
    document.body.classList.add("mode-minimal", `accent-${acc}`);
    document.body.classList.toggle("theme-dark", darkMode);
    localStorage.setItem("sb_mode", "minimal");
    localStorage.setItem("sb_theme", acc);
  }, [mode, theme, darkMode]);

  useEffect(() => { localStorage.setItem("sb_lang", lang); }, [lang]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { setAuthChecked(true); return; }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      if (res.data.theme_color) setThemeState(sanitizeAccent(res.data.theme_color));
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (!user) { setShopEnabled(false); return; }
    api.get("/shop/status").then((r) => setShopEnabled(!!r.data.enabled)).catch(() => setShopEnabled(false));
  }, [user]);

  useEffect(() => {
    if (!user) { setGroupsEnabled(false); return; }
    api.get("/groups/status").then((r) => setGroupsEnabled(!!r.data.enabled)).catch(() => setGroupsEnabled(false));
  }, [user]);

  useEffect(() => {
    if (!user) { setSupportEnabled(false); return; }
    api.get("/support/status").then((r) => setSupportEnabled(!!r.data.enabled)).catch(() => setSupportEnabled(false));
  }, [user]);

  useEffect(() => { localStorage.setItem("sb_cart", JSON.stringify(cart)); }, [cart]);

  const addToCart = (item) => setCart((prev) => {
    const idx = prev.findIndex((x) => x.key === item.key);
    if (idx >= 0) {
      const n = [...prev];
      n[idx] = { ...n[idx], quantity: Math.min(n[idx].quantity + item.quantity, item.max_qty || 999) };
      return n;
    }
    return [...prev, item];
  });
  const updateCartQty = (key, qty) => setCart((prev) => prev.map((x) => x.key === key
    ? { ...x, quantity: Math.max(x.min_qty || 1, Math.min(qty, x.max_qty || 999)) } : x));
  const removeFromCart = (key) => setCart((prev) => prev.filter((x) => x.key !== key));
  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((s, x) => s + x.quantity, 0);

  const loginWithToken = (token, u) => {
    if (token) localStorage.setItem("sb_token", token);
    setUser(u);
    if (u?.theme_color) setThemeState(sanitizeAccent(u.theme_color));
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sb_token");
    setUser(null);
  };

  return (
    <AppContext.Provider value={{ user, setUser, authChecked, lang, setLang, mode, setMode, theme, setTheme, THEMES, darkMode, setDarkMode, t, checkAuth, loginWithToken, logout, shopEnabled, setShopEnabled, groupsEnabled, setGroupsEnabled, supportEnabled, setSupportEnabled, content, loadContent, simpleMode, setSimpleMode, cart, addToCart, updateCartQty, removeFromCart, clearCart, cartCount }}>
      {children}
    </AppContext.Provider>
  );
}
